/**
 * @file OrderDetailTabs.tsx
 * @description Вкладки карточки заказа и их содержимое
 * (phase19.24-order-card-chain-design.md §5; вынесено из `OrderDetailPage`;
 * phase19.31 — общий `Tabs`, вкладки не пересоздаются).
 *
 * Состав вкладок — реестр `ORDER_TABS` (`hooks/useOrderTabParam.ts`), активная
 * вкладка и подсветка строки живут в адресе: ссылку на платёж можно переслать
 * коллеге, а F5 больше не сбрасывает вкладку.
 *
 * Открытая однажды вкладка остаётся смонтированной и только скрывается
 * (`KEEP_MOUNTED`): при возврате на неё прежние данные видны сразу, без
 * пустоты со спиннером, шторки и раскрытые строки не теряются; свежие данные
 * она дочитывает тихо (`useOrderTabRevisit`). «Лента» — так же: прежние
 * события видны сразу, свежие дочитываются поверх (phase19.31).
 *
 * «Доставка» (phase19.24 шаг 5.2) — единственный дом условий доставки. Точка
 * на вкладке загорается, когда ход наш (реестр `delivery-block-state`).
 * «Оплаты и документы» — одна вкладка: список документов показываем по
 * разрезу `useCanViewDocuments` (любое из трёх прав, как на сервере).
 */

import { useState, type ReactNode } from "react";

import { OrderDeliveryBlock } from "./delivery/OrderDeliveryBlock";
import type { AgreementSendRequest } from "./delivery/useDeliveryAgreementActions";
import { OrderDocumentsTab } from "./OrderDocumentsTab";
import { OrderFeedTab } from "./OrderFeedTab";
import { OrderItemsTab } from "./OrderItemsTab";
import { OrderOverviewTab } from "./OrderOverviewTab";
import { OrderPaymentsTab } from "./OrderPaymentsTab";
import { OrderReturnsTab } from "./OrderReturnsTab";
import { OrderShipmentsTab } from "./OrderShipmentsTab";
import { Tabs } from "../ui/Tabs";
import type { OrderDeliveryState } from "../../hooks/useOrderDelivery";
import { ORDER_TABS, type OrderTabKey } from "../../hooks/useOrderTabParam";
import type { DeliveryAgreementDto } from "../../lib/api-delivery-agreement";
import type { OrderDto } from "../../lib/api-orders";
import { useCanViewDocuments } from "../../lib/document-access";
import { deliveryBlockView } from "../../lib/order-delivery-view";

/** Вкладки, которые после первого открытия остаются смонтированными. */
const KEEP_MOUNTED: ReadonlySet<OrderTabKey> = new Set([
  "overview",
  "items",
  "delivery",
  "shipments",
  "payments",
  "returns",
  "activity",
]);

interface Props {
  order: OrderDto;
  agreement: DeliveryAgreementDto | null;
  /** Общий на карточку ответ `GET …/delivery` — один на обе вкладки. */
  delivery: OrderDeliveryState;
  canManage: boolean;
  /** Действие карточки выполняется — кнопки вкладок не дублируют переход. */
  acting?: boolean;
  /** Перевести заказ в согласование доставки (стадийный запрет во вкладке). */
  onAgreeDelivery?: () => void;
  slug: string | undefined;
  tab: OrderTabKey;
  /** id строки, на которую привёл deep-link из «Пути заказа». */
  focus: string | null;
  onTabChange: (key: OrderTabKey) => void;
  /** Полоса «Сейчас» позвала «Отправить на подтверждение» (вкладка «Доставка»). */
  sendRequest?: AgreementSendRequest | null;
  onSendHandled?: () => void;
  /** Перечитать заказ (тихо) — после платежей, отгрузок, возвратов, правок. */
  onChanged: () => void;
}

export function OrderDetailTabs(props: Props) {
  const { order, agreement, delivery, canManage, slug, tab, focus, onTabChange, onChanged } = props;
  const canViewDocuments = useCanViewDocuments();
  const [visited, setVisited] = useState<ReadonlySet<OrderTabKey>>(() => new Set([tab]));
  if (!visited.has(tab)) setVisited(new Set([...visited, tab]));

  // Точка на вкладке «Доставка» — тем же сведением, что и сам блок (шаг 5.2 §2).
  const deliveryDot = deliveryBlockView(order, delivery.data, agreement, {
    canManage,
    editing: false,
    quoteReason: null,
  }).meta.dot;

  const panel: Record<OrderTabKey, (active: boolean) => ReactNode> = {
    overview: () => <OrderOverviewTab order={order} shopSlug={slug} />,
    items: (active) => (
      <OrderItemsTab order={order} agreement={agreement} canManage={canManage} focusId={focus} onChanged={onChanged} active={active} />
    ),
    delivery: () => (
      <OrderDeliveryBlock
        order={order}
        agreement={agreement}
        delivery={delivery}
        canManage={canManage}
        shopSlug={slug}
        sendRequest={props.sendRequest}
        onSendHandled={props.onSendHandled}
        onAgreeDelivery={props.onAgreeDelivery}
        acting={props.acting}
        onChanged={onChanged}
      />
    ),
    shipments: (active) => (
      <OrderShipmentsTab
        order={order}
        agreement={agreement}
        delivery={delivery}
        canManage={canManage}
        shopSlug={slug}
        onChanged={onChanged}
        focusId={focus}
        onOpenDelivery={() => onTabChange("delivery")}
        active={active}
      />
    ),
    payments: (active) => (
      <div className="flex flex-col gap-4">
        <OrderPaymentsTab order={order} canManage={canManage} onChanged={onChanged} focusId={focus} active={active} />
        {/* `?focus=` может указывать и на платёж, и на документ: строку
            подсвечивает тот список, где такой id есть. */}
        {canViewDocuments ? <OrderDocumentsTab order={order} focusId={focus} active={active} /> : null}
      </div>
    ),
    returns: (active) => (
      <OrderReturnsTab order={order} canManage={canManage} onChanged={onChanged} focusId={focus} active={active} />
    ),
    activity: (active) => <OrderFeedTab order={order} active={active} />,
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Tabs
        aria-label="Разделы заказа"
        value={tab}
        onChange={onTabChange}
        items={ORDER_TABS.map((t) => ({
          value: t.key,
          label: t.label,
          // Точка «ход наш»: заказ не простоит из-за несогласованной доставки (§2).
          marker: t.key === "delivery" && deliveryDot ? "требует вашего решения" : undefined,
        }))}
      />

      {ORDER_TABS.map(({ key }) => {
        const active = key === tab;
        const mounted = active || (visited.has(key) && KEEP_MOUNTED.has(key));
        if (!mounted) return null;
        return (
          <div key={key} role="tabpanel" aria-label={ORDER_TABS.find((t) => t.key === key)?.label} hidden={!active}>
            {panel[key](active)}
          </div>
        );
      })}
    </div>
  );
}
